import type { ProductNature } from '@/types/storebag/product'

export type PartnerCampaignStatus = 'draft' | 'active' | 'archived'

/** Synthese consolidee hydratee serveur (participants/budgets/contrats) d'une campagne. */
export type PartnerCampaignSummary = {
  soundParticipants: number
  soundBudget: number
  plvParticipants: number
  plvBudget: number
  pharmacies: number
  allocated: number
  contractsOk: number
  contractsKo: number
  // Compteurs de participation (workflow v2) : les refuses sont EXCLUS des compteurs
  // participants/pharmacies ci-dessus, d'ou l'affichage separe « · N refus ».
  participationAccepted?: number
  participationPending?: number
  /**
   * VRAIS refus seulement (02/10/2026) : un budget ferme par echeance — delai de signature
   * expire — n'y entre plus, il est compte a part ci-dessous. Acceptes + en attente + refuses
   * + expires = le nombre de budgets.
   */
  participationRejected?: number
  /** BUDGETS fermes par echeance : ni acceptes ni refuses — a libeller « délai expiré ». */
  participationExpired?: number
  /** OFFICINES dont plus rien ne vit dans la campagne a cause de l'echeance. */
  expiredPharmacies?: number
}

// Campagne multi-programme partenaire (proposition de repartition L2). Une campagne regroupe
// des programmes Sound et/ou StoreBag (references par IRI) rattaches a un meme donneur d'ordre.
export type PartnerCampaign = {
  id: number
  partner: string
  name: string
  /**
   * NOM AFFICHE AU PHARMACIEN (contrats, « Mes partenaires »). Absent ou `null` = le
   * pharmacien lit `name`. E-mails : rendu par `{{program_name}}` d'un e-mail de campagne
   * seulement ; `{{campaign_name}}` rend toujours `name`.
   */
  nameShort?: string | null
  /** Nom interne : conserve sur la campagne, affiche sur aucun ecran. */
  nameInternal?: string | null
  dateStart: string
  dateEnd: string
  status: PartnerCampaignStatus
  budgetTotal: number | null
  soundProgrammes: string[]
  storeBagPrograms: string[]
  /**
   * Programmes EN CLAIR (nom + statut propre). Absent d'une reponse d'API anterieure au
   * 08/09/2026 : l'ecran retombe alors sur les IRI ci-dessus.
   */
  programsSummary?: PartnerCampaignProgramSummary[]
  summary?: PartnerCampaignSummary | null
  createdAt: string
  /**
   * DATE LIMITE DE SIGNATURE du contrat de campagne (jour INCLUS, 23:59 heure de Paris), au
   * format ISO d'une colonne DATE (`2026-10-07T00:00:00+00:00`). Absente ou `null` = la
   * campagne n'a pas de date limite (campagne anterieure, ou laboratoire sans le reglage).
   * A lire par `toDayString()` / `formatCalendarDay()` (`lib/campaign/signatureDeadline.ts`),
   * jamais par `new Date()` : c'est un jour, pas un instant.
   */
  signatureDeadline?: string | null
  /**
   * REMISE RADIO + PLV (%) FIGEE a la creation de la campagne, comme les trois autres tarifs :
   * la fiche laboratoire peut changer ensuite, la campagne garde la sienne. Lecture seule (le
   * PATCH de la campagne ne la modifie pas) ; absente d'une campagne non reprise. Les couts
   * la restituent dans `costs.fees.radioPlvDiscountPercent`, avec son montant par officine.
   */
  radioPlvDiscountPercent?: number
  /** Mode de repartition fige a la creation : `dual_axis` = deux enveloppes (GX, BS). */
  allocationMode?: 'single' | 'dual_axis' | null
  /**
   * FACTURE RADIO UNIQUE (21/10/2026) : une officine sur les deux programmes radio (GX et BS)
   * recoit une seule facture radio pour la campagne (laboratoire facture hors plateforme).
   * Modifiable par le PATCH de la campagne ; ne change rien a une facture deja emise.
   */
  singleRadioInvoice?: boolean
}

/**
 * Reponse de `GET partner_campaigns/signature_deadline_proposal` : la date limite que le
 * SERVEUR posera sur une campagne a creer (debut + N − 1 jours). `signatureDeadline` est
 * `null` quand le laboratoire n'a pas le reglage actif — la campagne nait alors sans date.
 * Jours au format `AAAA-MM-JJ`.
 */
export type CampaignSignatureDeadlineProposal = {
  enabled: boolean
  /** N effectif : « signature jusqu'au Nᵉ jour du programme » (7 par defaut). */
  days: number
  dateStart: string
  signatureDeadline: string | null
}

/**
 * Champ « date limite de signature » de l'identite de la campagne, tel que le rend
 * `CampaignIdentityFields` et que l'expose `useCampaignWizard`. `null` cote assistant = le
 * champ n'a pas lieu d'etre (reglage du laboratoire coupe, ou vague rattachee a une campagne
 * existante, qui garde sa date).
 */
export type CampaignSignatureDeadlineField = {
  /** Date affichee (`AAAA-MM-JJ`) ; `''` = campagne creee SANS date limite. */
  value: string
  /** Date proposee par le serveur pour la date de debut courante (`null` pendant le calcul). */
  proposed: string | null
  /** N effectif du laboratoire : « signature jusqu'au Nᵉ jour du programme ». */
  days: number
  /** Seul un ADMIN change la date ; un laboratoire voit celle qui s'appliquera. */
  editable: boolean
  /** La proposition du serveur est en cours de calcul (date de debut modifiee). */
  loading: boolean
  /** La date affichee n'est plus celle que le serveur propose. */
  overridden: boolean
  onChange: (value: string) => void
  /** Revient a la date proposee. */
  onReset: () => void
}

/**
 * Reponse de `POST partner_campaigns/:campaignId/signature_deadline` — la MEME forme pour
 * l'apercu (`dryRun: true`, rien n'est ecrit) et pour l'ecriture. Jours au format `AAAA-MM-JJ`.
 *
 * Trois populations, en officines ET en budgets (une officine porte jusqu'a trois budgets
 * dans une campagne : radio GX, radio BS, PLV) :
 *  - `pending*`    : contrat de campagne encore a signer aujourd'hui ;
 *  - `closing*`    : parmi elles, celles que la date demandee fermera (date deja passee) ;
 *  - `expired*`    : deja fermees par echeance ; `reopenable*` : celles qu'une date non passee
 *                    (ou retiree) rouvre ; `reopened*` : REELLEMENT rouvertes par l'ecriture.
 */
export type CampaignSignatureDeadlineResult = {
  campaignId: number
  dryRun: boolean
  /** La date a-t-elle ete ECRITE par cet appel (faux en apercu, faux si elle etait deja celle-la). */
  applied: boolean
  /** Date limite AVANT l'appel (`null` = aucune). */
  currentSignatureDeadline: string | null
  /** Date demandee (apercu) ou desormais en vigueur (ecriture). `null` = aucune. */
  signatureDeadline: string | null
  /** Jour courant a Paris, base des comptages. */
  today: string
  /** La date demandee est-elle strictement avant aujourd'hui ? */
  deadlinePassed: boolean
  pendingPharmacies: number
  pendingBudgets: number
  closingPharmacies: number
  closingBudgets: number
  expiredPharmacies: number
  expiredBudgets: number
  reopenablePharmacies: number
  reopenableBudgets: number
  reopenedPharmacies: number
  reopenedBudgets: number
  /**
   * Officines et budgets REELLEMENT fermes par l'ecriture (date deja passee, fermeture
   * immediate du 03/10/2026), y compris quand la date etait deja celle-la (`applied` faux).
   * Toujours 0 en apercu ; absents d'une API anterieure.
   */
  closedPharmacies?: number
  closedBudgets?: number
  // Stock PLV (cles ajoutees par le lot A3, apres le lot F1) — optionnelles : une API
  // anterieure ne les sert pas. Etat constate AVANT l'ecriture, en apercu comme en ecriture.
  /** Unites PLV que les fermetures annoncees (`closing*`) liberent de l'engage. */
  releasedStockUnits?: number
  /** Unites PLV que les reouvertures annoncees (`reopenable*`) re-engagent. */
  reengagedStockUnits?: number
  /** Au moins un produit dont le disponible passe SOUS ZERO apres le geste. */
  stockShortage?: boolean
  /** Une ligne par produit PLV touche. */
  stock?: CampaignSignatureStockLine[]
}

/**
 * Ligne de stock d'un produit PLV dans l'apercu d'une reouverture : ce que le geste RE-ENGAGE.
 * L'« engage » n'est pas une reservation, c'est une information recalculee a chaque lecture
 * pour estimer le disponible — rien n'est ecrit dans la boutique.
 */
export type CampaignSignatureStockLine = {
  productId: number
  productName: string | null
  /** Stock boutique ; `null` = inconnu ou non suivi (jamais 0). */
  storeStock: number | null
  /** Engage AUJOURD'HUI sur tout le parc, avant le geste. */
  engaged: number
  /** Unites que la reouverture remet dans l'engage. */
  reengaged: number
  /** Toujours 0 pour une reouverture (champ commun aux gestes qui liberent du stock). */
  released: number
  /** Stock − engage − re-engage ; `null` si le stock est inconnu. */
  availableAfter: number | null
  /** Le disponible passe SOUS ZERO apres le geste. */
  shortage: boolean
}

/**
 * Reponse de `POST partner_campaigns/:campaignId/signature_reopening` — la MEME forme pour
 * l'apercu (`dryRun: true`, rien n'est ecrit) et pour l'ecriture. Jours au format `AAAA-MM-JJ`.
 * Le grain est le GROUPE (officine × campagne) : tous les budgets radio et PLV de l'officine
 * dans la campagne se rouvrent ensemble, sous une meme date derogatoire.
 */
export type CampaignSignatureReopeningResult = {
  campaignId: number
  pharmacyId: number
  pharmacyName: string | null
  dryRun: boolean
  /** La reouverture a-t-elle ete ECRITE par cet appel (faux en apercu). */
  applied: boolean
  /** Jour courant a Paris. */
  today: string
  /** Nouvelle date limite de l'officine sur cette campagne, jour INCLUS. */
  until: string
  /** Date qui s'appliquait a l'officine AVANT (derogation en cours, sinon date de campagne). */
  currentSignatureDeadline: string | null
  /** Budgets fermes par la tache quotidienne dont la fermeture est (ou serait) levee. */
  expiredBudgets: number
  /** Budgets du groupe qui recoivent la nouvelle date (tous, hors vrais refus). */
  datedBudgets: number
  /** Budgets REELLEMENT rouverts par cet appel. Toujours 0 en apercu. */
  reopenedBudgets: number
  /** Programmes radio dont la repartition est (ou serait) relancee. */
  radioProgrammes: number
  /** Unites PLV que la reouverture re-engage, tous produits confondus. */
  reengagedUnits: number
  /** Au moins un produit dont le disponible passe SOUS ZERO apres re-engagement. */
  stockShortage: boolean
  /** Une ligne par produit PLV re-engage ; vide quand la reouverture ne re-engage rien. */
  stock: CampaignSignatureStockLine[]
}

/** IRI API Platform d'une campagne (« /api/v2/partner_campaigns/42 »). */
export const partnerCampaignIri = (id: number): string => `/api/v2/partner_campaigns/${id}`

/**
 * Nombre maximal de programmes radio (Sound) par vague de campagne. Miroir de
 * `CampaignAllocationConfig::MAX_SOUND_PROGRAMS` cote API (au-dela : 422).
 */
export const MAX_SOUND_PROGRAMS = 4

/**
 * Programme radio EN COURS DE SAISIE dans l'assistant de campagne. `key` est une identite
 * React stable (ajout/suppression sans perdre les champs des autres cartes) ; `label` peut
 * etre vide (resolu en « Programme {i} » a l'envoi).
 */
export type SoundProgramDraft = {
  key: string
  label: string
  /** Preset de type de programme Sound (ex. 'custom', 'lite', 'intense'). */
  type: string
  /** Ids SoundSpot du catalogue du labo a diffuser (peut etre vide). */
  spotIds: number[]
  /**
   * Axe de campagne du programme (laboratoires en mode `dual_axis` uniquement) : `gx` =
   * Generique, `bs` = Biosimilaire. Absent hors dual — la factory serveur laisse alors
   * `campaignAxis` a `null` (D-4-12 du lot 4).
   */
  axis?: ProposalAxisKey
}

/** Programme radio tel qu'ENVOYE a l'API (`soundPrograms[]`) : label toujours resolu. */
export type SoundProgramInput = {
  label: string
  type: string
  spotIds: number[]
  /**
   * `'gx'` | `'bs'`, OPTIONNEL (contrat lot 4, `report-4.md` §3.4). Absent/vide => l'API
   * derive l'axe de l'index (1 = GX, 2 = BS) ; une valeur inconnue est un 422.
   */
  axis?: ProposalAxisKey
}

/** Spot du catalogue du labo (GET partner_campaigns/spot_catalog). `type` = 'chronique' pour une chronique. */
export type SoundSpotCatalogEntry = {
  id: number
  name: string
  type: string
}

/** Valeur de `SoundSpotCatalogEntry.type` d'une chronique (SoundSpot::TYPE_SPOT cote API). */
export const SOUND_SPOT_TYPE_CHRONIC = 'chronique'

/** Type « Personnalise » (SoundProgramme::TYPE_CUSTOM cote API) : frequences deduites des spots. */
export const SOUND_PROGRAM_TYPE_CUSTOM = 'custom'

/**
 * Type legacy « manuel » (SoundProgramme::TYPE_FREQUENCyCHRONIC_NB_SPOT_MANUAL cote API) :
 * aucun plafond de spots, chronique acceptee.
 */
export const SOUND_PROGRAM_TYPE_MANUAL = 'manual'

/** Preset d'un type de programme Sound (GET sound_programme_types, source unique serveur). */
export type SoundProgramTypePreset = {
  value: string
  spotsPerHour: number
  chronicFrequencyPerHour: number
  /** Annonces au demarrage du player : hors slots horaires, jamais de chronique. */
  preroll: boolean
  /** Frequences libres : le nombre de spots selectionnes fait la frequence. */
  custom: boolean
}

/** Verdict du controle de capacite d'un programme radio en cours de saisie. */
export type SoundProgramCapacityCheck = {
  /** Spots selectionnes hors chronique (comptes sur le catalogue du labo). */
  spotCount: number
  /** Chroniques selectionnees (comptees sur le catalogue du labo). */
  chronicCount: number
  /** Type a frequence fixe : plus de spots (hors chronique) que sa capacite spots/h. */
  overCapacity: boolean
  /** Une chronique est selectionnee alors que le type n'en diffuse pas. */
  chronicNotAllowed: boolean
}

/**
 * Miroir front de `SoundProgrammeSpotCapacityService::getBlockingErrors` (API) : a l'import,
 * un programme dont les spots ne tiennent pas dans le parametrage de son type est refuse
 * (422 apres le 202, simple email d'echec). Memes regles ici pour prevenir en amont :
 * - custom : les frequences se deduisent de la selection, jamais bloquant ;
 * - preroll : aucun plafond de spots, mais jamais de chronique ;
 * - manual (legacy) : aucun plafond, chronique acceptee ;
 * - autres presets : au plus `spotsPerHour` spots hors chronique, et aucune chronique quand
 *   `chronicFrequencyPerHour` vaut 0 (lite2, intense3, sandoz).
 * Preset inconnu (liste pas encore chargee) : aucun verdict, comptes seulement.
 */
export const checkSoundProgramCapacity = (
  draft: Pick<SoundProgramDraft, 'type' | 'spotIds'>,
  preset: SoundProgramTypePreset | undefined,
  spotCatalog: SoundSpotCatalogEntry[]
): SoundProgramCapacityCheck => {
  // Comptage sur le CATALOGUE : un id de spot disparu du labo ne compte pas.
  const selectedSpots = spotCatalog.filter(spot => draft.spotIds.includes(spot.id))
  const chronicCount = selectedSpots.filter(spot => spot.type === SOUND_SPOT_TYPE_CHRONIC).length
  const spotCount = selectedSpots.length - chronicCount
  // Le preset fait foi ; avant son chargement, le type par defaut custom reste libre.
  const isCustom = preset?.custom ?? draft.type === SOUND_PROGRAM_TYPE_CUSTOM

  if (isCustom || preset == null) {
    return { spotCount, chronicCount, overCapacity: false, chronicNotAllowed: false }
  }

  const unlimited = preset.preroll || preset.value === SOUND_PROGRAM_TYPE_MANUAL

  return {
    spotCount,
    chronicCount,
    overCapacity: !unlimited && spotCount > preset.spotsPerHour,
    chronicNotAllowed:
      chronicCount > 0 && (preset.preroll || (!unlimited && preset.chronicFrequencyPerHour === 0)),
  }
}

/** Fabrique le libelle par defaut d'un programme (« Programme {i} »), fourni par l'i18n. */
export type SoundProgramDefaultLabel = (index: number) => string

/**
 * Libelle affiche d'un programme : label saisi (trim) ou libelle par defaut « Programme {i} ».
 * Le texte par defaut vient de l'i18n de l'appelant — aucune chaine en dur ici.
 *
 * @param index Position 1-based du programme dans l'assistant.
 */
export const soundProgramDisplayLabel = (
  draft: Pick<SoundProgramDraft, 'label'>,
  index: number,
  defaultLabel: SoundProgramDefaultLabel
): string => {
  const trimmed = draft.label.trim()

  return trimmed !== '' ? trimmed : defaultLabel(index)
}

/**
 * Brouillons -> payload API : label resolu (trim ou « Programme {i} »), type et spots tels
 * quels. L'ordre est conserve : il definit l'index de programme (colonnes du fichier).
 */
export const toSoundProgramInputs = (
  drafts: SoundProgramDraft[],
  defaultLabel: SoundProgramDefaultLabel
): SoundProgramInput[] =>
  drafts.map((draft, i) => ({
    label: soundProgramDisplayLabel(draft, i + 1, defaultLabel),
    type: draft.type,
    spotIds: draft.spotIds,
    // `axis` n'est envoye QUE s'il est defini : hors dual_axis la cle absente laisse le
    // serveur poser `campaignAxis = null` (une chaine vide leverait une ValueError PHP).
    ...(draft.axis ? { axis: draft.axis } : {}),
  }))

// ---------------------------------------------------------------------------------------
// Campagne a DEUX ENVELOPPES (GX / BS) — contrat serveur des lots 2b / 4 / 5.
// Formes JSON exactes : `report-2b.md` §7 (rows/summary dual), `report-4.md` §3 (anomalies,
// pharmacyContext, countAnomalies, settings), `report-5.md` §4 (mode, runId, allocation_runs).
// ---------------------------------------------------------------------------------------

/** Mode de repartition du LABORATOIRE (`Company.campaignAllocationMode`, lot 0). */
/**
 * Programme d'une campagne, EN CLAIR (`programsSummary`, 08/09/2026). Les collections de
 * programmes ne voyagent qu'en IRI : sans ce resume, l'ecran ne pouvait afficher qu'un
 * libelle de module. `status` est celui du PROGRAMME, jamais celui de la campagne.
 */
export type PartnerCampaignProgramSummary = {
  id: number
  module: 'sound' | 'store_bag'
  name: string | null
  /** `draft` | `onGoing` | `terminated` — vocabulaire commun aux deux modules. */
  status: string
}

/** Statuts d'un programme qui peut encore recevoir des pharmacies (decision D-1, 19/10/2026). */
export const APPENDABLE_PROGRAM_STATUSES = ['draft', 'onGoing']

/** Destination d'un volet en mode ajout : id d'un programme existant, ou nouveau programme. */
export const NEW_PROGRAM_CHOICE = 'new'
export type CampaignDestinationChoice = number | typeof NEW_PROGRAM_CHOICE

/**
 * Programmes EXISTANTS vises par un import (payload `targetPrograms`, 19/10/2026) : numero de
 * colonne radio => id de programme Sound, et id du programme PLV.
 */
export type CampaignTargetPrograms = {
  sound?: Record<string, number>
  plv?: number
}

/**
 * Apercu de l'ajout, par programme vise (`targets[]` de la prevalidation) : lignes du fichier,
 * pharmacies deja presentes (ecartees) et pharmacies qui seront ajoutees.
 */
export type CampaignAppendTargetPreview = {
  kind: 'sound' | 'plv'
  /** Colonne radio (1..N) ; null pour la PLV. */
  index: number | null
  programmeId: number
  name: string
  status: string | null
  rows: number
  alreadyPresent: number
  toAdd: number
  alreadyPresentCips: string[]
}

/**
 * FACTURE RADIO UNIQUE (22/10/2026) : ce que l'ajout change pour la facturation radio des
 * officines ajoutees (`radioInvoice` de la prevalidation). Cle ABSENTE quand rien n'est a
 * annoncer — interrupteur de campagne eteint, programme sans axe ou sur plateforme, aucune
 * officine concernee. Chiffres calcules par le serveur, en lot, pour tout le fichier ; une
 * officine n'est comptee que dans UN des trois effets.
 */
export type CampaignAppendRadioInvoicePreview = {
  /** Officines ajoutees qui se retrouvent sur les deux axes (GX et BS) : une seule facture radio. */
  singleInvoice: number
  /**
   * Officines ajoutees dont le groupe GX + BS serait mal forme (deux budgets d'un meme axe, deux
   * titulaires) : leur facture radio n'est jamais generee d'office, elle est a arbitrer.
   */
  toArbitrate: number
  /** Officines ajoutees dont un budget de la campagne est deja facture : le nouveau se facture a part. */
  complementInvoice: number
}

export type CampaignAllocationMode = 'single' | 'dual_axis'

/**
 * Les SIX regles de calcul reglables d'une repartition a deux enveloppes (lot 1).
 *
 * Elles sont prechargees depuis la fiche du laboratoire
 * (`GET partner_campaigns/allocation_settings`), modifiables POUR UN CALCUL, envoyees avec la
 * proposition ET avec l'export XLSX (sans quoi le classeur telecharge divergerait de l'ecran),
 * puis figees dans `params.settings` de la trace du calcul.
 *
 * `newInstallationThreshold` : `null` = nouvelle pose radio DESACTIVEE (ce n'est pas
 * « valeur par defaut »), conformement a `CampaignAllocationSettings::isNewPoseEnabled()`.
 */
export type CampaignAllocationNumericSettings = {
  logisticsAdminFee: number
  radioMonthlyFee: number
  radioSetupFee: number
  newInstallationThreshold: number | null
  defaultMaxUnitsPerPharmacy: number
  fixedPlvMaxPerProduct: number
  /**
   * REMISE RADIO + PLV, en pourcentage (0 a 100) — 4ᵉ tarif, arrive le 02/10/2026. Une officine
   * qui a la radio ET de la PLV dans la meme campagne paie le forfait logistique plus la gestion
   * radio REMISEE de ce pourcentage (contrat : 15,17 + 39 − 10 % = 50,27 €). L'assiette est la
   * gestion radio seule — ni le forfait logistique, ni la pose. `0` = aucune remise.
   * FACULTATIF : une API ou un calcul anterieur ne le porte pas ; le bloc de regles ne
   * l'affiche et ne l'envoie alors pas.
   */
  radioPlvDiscountPercent?: number
  /**
   * SEUIL DE PREFERENCE RADIO, sur le TOTAL GX + BS de l'officine (demande Zentiva du 30/10/2026) :
   * en dessous, la PLV passe devant, meme avec une radio en place. `null` = regle desactivee.
   * FACULTATIF : une API ou un calcul anterieur ne le porte pas.
   */
  radioPreferenceThreshold?: number | null
}

/**
 * Les criteres du remplissage exact des consommables, dans l'ordre ou le moteur les
 * applique (`CampaignConsumableExactFiller`).
 *
 * - `gap`       : ecart minimal — consommer l'enveloppe au plus pres (reliquat le plus faible) ;
 * - `shipments` : moins d'envois — le moins de references distinctes expediees a l'officine ;
 * - `catalog`   : ordre du catalogue — position maximale, puis somme position x quantite ;
 * - `spread`    : repartir — a ecart egal, poser moins d'unites d'une meme reference ;
 * - `supply`    : cout de fourniture — a ecart egal, la composition dont le cout MATIERE est le
 *   plus bas. Ce cout n'est pas proportionnel au montant (de 1,5 % a 6,7 % du prix de vente
 *   selon la reference) : tant que le critere reste en queue, il subit le departage des autres.
 *
 * L'ordre par defaut (`gap` -> `shipments` -> `catalog` -> `spread` -> `supply`) reproduit
 * STRICTEMENT le comportement anterieur : deplacer `gap` change ce qu'on accepte de reliquat,
 * pas seulement une preference d'affichage.
 */
export type ConsumableCriterion = 'gap' | 'shipments' | 'catalog' | 'spread' | 'supply'

/**
 * Catalogue des cles de criteres. C'est le catalogue MAXIMAL connu du front : le serveur rend
 * le sien dans `allocation_settings.criteria`, et c'est LUI qui fait foi pour ce qu'on affiche
 * et ce qu'on envoie — une API anterieure a `spread` n'en declare que trois.
 */
export const CONSUMABLE_CRITERIA: readonly ConsumableCriterion[] = [
  'gap',
  'shipments',
  'catalog',
  'spread',
  'supply',
] as const

/**
 * Longueur MINIMALE d'un ordre encore exploitable. Un run joue — ou une fiche labo enregistree —
 * avant l'arrivee de `spread` puis de `supply` n'en porte que trois : c'est une valeur legitime,
 * qu'on complete en fin de liste plutot que de la jeter.
 */
export const CONSUMABLE_CRITERIA_MIN_LENGTH = 3

/** Ordre par defaut du moteur = comportement d'avant le reglage. */
export const DEFAULT_CONSUMABLE_CRITERIA_ORDER: readonly ConsumableCriterion[] = [
  'gap',
  'shipments',
  'catalog',
  'spread',
  'supply',
] as const

/** Cles i18n du libelle, du libelle COURT (resume d'une ligne) et de l'aide, par critere. */
export const CONSUMABLE_CRITERION_LABEL_KEYS: Record<ConsumableCriterion, string> = {
  gap: 'components.partnerCampaign.proposal.rules.criteriaGap',
  shipments: 'components.partnerCampaign.proposal.rules.criteriaShipments',
  catalog: 'components.partnerCampaign.proposal.rules.criteriaCatalog',
  spread: 'components.partnerCampaign.proposal.rules.criteriaSpread',
  supply: 'components.partnerCampaign.proposal.rules.criteriaSupply',
}
export const CONSUMABLE_CRITERION_SHORT_KEYS: Record<ConsumableCriterion, string> = {
  gap: 'components.partnerCampaign.proposal.rules.criteriaShortGap',
  shipments: 'components.partnerCampaign.proposal.rules.criteriaShortShipments',
  catalog: 'components.partnerCampaign.proposal.rules.criteriaShortCatalog',
  spread: 'components.partnerCampaign.proposal.rules.criteriaShortSpread',
  supply: 'components.partnerCampaign.proposal.rules.criteriaShortSupply',
}
export const CONSUMABLE_CRITERION_HELP_KEYS: Record<ConsumableCriterion, string> = {
  gap: 'components.partnerCampaign.proposal.rules.criteriaGapHelp',
  shipments: 'components.partnerCampaign.proposal.rules.criteriaShipmentsHelp',
  catalog: 'components.partnerCampaign.proposal.rules.criteriaCatalogHelp',
  spread: 'components.partnerCampaign.proposal.rules.criteriaSpreadHelp',
  supply: 'components.partnerCampaign.proposal.rules.criteriaSupplyHelp',
}

/** Separateur du resume « ecart › envois › catalogue » (glyphe, pas une phrase a traduire). */
export const CONSUMABLE_CRITERIA_SEPARATOR = ' › '

export const isConsumableCriterion = (value: unknown): value is ConsumableCriterion =>
  typeof value === 'string' && (CONSUMABLE_CRITERIA as readonly string[]).includes(value)

/**
 * Cles CONNUES et DISTINCTES, en nombre suffisant — memes regles que le 422 du serveur, a ceci
 * pres qu'un ordre de TROIS cles reste accepte : c'est ce que portent les runs et les fiches
 * labo anterieurs a `spread`. Un doublon, une cle inconnue ou une liste trop courte restent
 * refuses : ils laisseraient un critere jamais applique sans que rien ne le dise.
 */
export const isConsumableCriteriaOrderValid = (value: unknown): value is ConsumableCriterion[] =>
  Array.isArray(value) &&
  value.length >= CONSUMABLE_CRITERIA_MIN_LENGTH &&
  value.length <= CONSUMABLE_CRITERIA.length &&
  value.every(isConsumableCriterion) &&
  new Set(value).size === value.length

/**
 * Ordre exploitable a partir de n'importe quelle valeur serveur, RAMENE au catalogue passe en
 * second argument (par defaut, tout ce que le front connait).
 *
 * Deux corrections y sont faites, dans cet ordre :
 * 1. une cle absente du catalogue est retiree — un ordre qui nomme `spread` face a une API qui
 *    ne le connait pas ferait un 422 au bout d'un calcul long ;
 * 2. une cle du catalogue absente de la valeur est AJOUTEE EN FIN, dans l'ordre du moteur : un
 *    ordre a trois cles enregistre avant `spread` reste lisible, et le nouveau critere arrive
 *    en dernier, la ou il ne change rien a ce qui etait deja arbitre.
 *
 * Une valeur absente ou corrompue retombe entierement sur le DEFAUT du moteur.
 */
export const normalizeConsumableCriteriaOrder = (
  value: unknown,
  catalog: readonly ConsumableCriterion[] = CONSUMABLE_CRITERIA
): ConsumableCriterion[] => {
  const allowed = catalog.length > 0 ? catalog : CONSUMABLE_CRITERIA
  const base = isConsumableCriteriaOrderValid(value)
    ? value.filter(criterion => allowed.includes(criterion))
    : []
  const missing = DEFAULT_CONSUMABLE_CRITERIA_ORDER.filter(
    criterion => allowed.includes(criterion) && !base.includes(criterion)
  )

  return [...base, ...missing]
}

/**
 * Les SIX valeurs numeriques + l'ORDRE des criteres consommables (lot « ordre des regles »).
 * L'ordre voyage exactement comme les six autres reglages : defaut moteur, fiche du labo,
 * surcharge pour un calcul, trace du run.
 */
export type CampaignAllocationSettings = CampaignAllocationNumericSettings & {
  consumableCriteriaOrder: ConsumableCriterion[]
  /**
   * SCENARIO B — « la meme repartition si tous les produits selectionnes etaient en stock »,
   * c'est-a-dire l'ecart qu'on obtiendrait apres reassort. DESACTIVE par defaut : c'est une
   * lecture d'appoint, qui coute une colonne a l'ecran et une feuille dans le classeur.
   * Absent = API anterieure au reglage (le scenario B y etait toujours calcule).
   */
  scenarioBEnabled?: boolean
}

/** Cles des six reglages NUMERIQUES, dans l'ordre d'affichage du bloc « Regles de calcul ». */
export const CAMPAIGN_ALLOCATION_SETTINGS_KEYS = [
  'logisticsAdminFee',
  'radioMonthlyFee',
  'radioSetupFee',
  'newInstallationThreshold',
  'radioPreferenceThreshold',
  'defaultMaxUnitsPerPharmacy',
  'fixedPlvMaxPerProduct',
  'radioPlvDiscountPercent',
] as const satisfies readonly (keyof CampaignAllocationNumericSettings)[]

/**
 * Reglages qu'une API ANTERIEURE ne declare pas dans `bounds` : sans borne, le champ n'est ni
 * affiche, ni valide, ni envoye — le calcul reste celui que cette API sait faire. Un reglage
 * OBLIGATOIRE laisse vide est, lui, une saisie invalide.
 */
export const CAMPAIGN_ALLOCATION_SETTINGS_OPTIONAL_KEYS: readonly (keyof CampaignAllocationNumericSettings)[] =
  ['radioPlvDiscountPercent', 'radioPreferenceThreshold'] as const

/** Bornes de validation d'un reglage (le serveur repond 422 hors bornes). */
export type CampaignAllocationSettingsBound = {
  min: number
  max: number
  /** Les deux seuils (`newInstallationThreshold`, `radioPreferenceThreshold`) acceptent `null`. */
  nullable?: boolean
}

/**
 * Reponse de `GET partner_campaigns/allocation_settings?industry=<id>`.
 *
 * `editable: false` (labo en enveloppe unique) : le bloc de regles n'est pas affiche.
 * `canSaveAsDefault` est tranche par le SERVEUR (`is_granted('ROLE_ADMIN')`) — jamais par le
 * front : `CompanyPostAndPatchProcessor::restoreAdminOnlyFields()` annulerait silencieusement
 * la saisie d'un Siege labo.
 */
export type CampaignAllocationSettingsView = {
  mode: CampaignAllocationMode
  editable: boolean
  canSaveAsDefault: boolean
  /** Valeurs REELLEMENT appliquees : fiche labo, puis defaut moteur pour ce qui manque. */
  effective: CampaignAllocationSettings
  /** Ce que porte la fiche du labo (`null` = non renseigne). */
  companyDefaults: { [K in keyof CampaignAllocationNumericSettings]: number | null } & {
    /** `null` = la fiche ne fixe pas d'ordre : le moteur applique le sien. */
    consumableCriteriaOrder?: ConsumableCriterion[] | null
  }
  /** Defauts du moteur, pour information. */
  engineDefaults: CampaignAllocationSettings
  /** Constantes FIXES du moteur, affichees en lecture seule (D-02). */
  readOnly: {
    deferredConsumableGapThreshold: number
    maxCipsDualAxis: number
  }
  /** Une cle FACULTATIVE du type (remise radio + PLV) est absente d'une API anterieure. */
  bounds: { [K in keyof CampaignAllocationNumericSettings]: CampaignAllocationSettingsBound }
  /**
   * Catalogue des cles de criteres reordonnables. ABSENT = API anterieure a ce lot : le front
   * n'affiche alors pas la liste et n'envoie aucun ordre (calcul strictement inchange).
   */
  criteria?: ConsumableCriterion[]
}

/** Cles a valeur ENTIERE : un plafond d'unites ne se saisit pas avec des decimales. */
export const CAMPAIGN_ALLOCATION_SETTINGS_INTEGER_KEYS: readonly (keyof CampaignAllocationNumericSettings)[] =
  ['defaultMaxUnitsPerPharmacy', 'fixedPlvMaxPerProduct'] as const

/**
 * Saisie a l'ecran des six regles : des CHAINES.
 *
 * Un champ vide n'est pas `0` — pour `newInstallationThreshold` il vaut « fonction desactivee »,
 * pour les cinq autres il est invalide. Garder des nombres dans l'etat rendrait ces deux cas
 * indiscernables et transformerait un champ en cours d'effacement en `0` euro.
 */
export type CampaignAllocationSettingsDraft = {
  [K in keyof CampaignAllocationNumericSettings]: string
} & {
  /** L'ordre des criteres ne se saisit pas au clavier : c'est une liste qu'on reordonne. */
  consumableCriteriaOrder: ConsumableCriterion[]
  /** Le scenario B est une case a cocher, pas une saisie : un booleen, jamais une chaine. */
  scenarioBEnabled: boolean
}

/**
 * Valeurs effectives -> saisie. `null` (seuil desactive) devient la chaine vide.
 *
 * `catalog` = les criteres que le SERVEUR declare (`allocation_settings.criteria`) : la saisie
 * ne doit jamais proposer un critere que l'API refuserait.
 */
export const toAllocationSettingsDraft = (
  settings: CampaignAllocationSettings,
  catalog?: readonly ConsumableCriterion[]
): CampaignAllocationSettingsDraft =>
  CAMPAIGN_ALLOCATION_SETTINGS_KEYS.reduce(
    (draft, key) => {
      const value = settings?.[key]
      draft[key] = value === null || value === undefined ? '' : String(value)

      return draft
    },
    {
      // Ordre absent (API anterieure) = defaut moteur : le calcul reste celui d'aujourd'hui.
      consumableCriteriaOrder: normalizeConsumableCriteriaOrder(
        settings?.consumableCriteriaOrder,
        catalog
      ),
      // Reglage absent (API anterieure) : le scenario B y etait toujours calcule.
      scenarioBEnabled: settings?.scenarioBEnabled ?? true,
    } as CampaignAllocationSettingsDraft
  )

/** Motif de refus d'une saisie : format inexploitable, ou hors des bornes du serveur. */
export type CampaignAllocationSettingsErrorCode = 'invalid' | 'range'

export type CampaignAllocationSettingsParse = {
  /** `null` des qu'une seule valeur est refusee : on n'envoie jamais un jeu incomplet. */
  values: CampaignAllocationSettings | null
  errors: Partial<Record<keyof CampaignAllocationSettings, CampaignAllocationSettingsErrorCode>>
}

/**
 * Saisie -> valeurs, avec les MEMES bornes que le serveur (rendues par `allocation_settings`).
 * La virgule decimale est acceptee : c'est la touche du pave numerique en France.
 */
export const parseAllocationSettingsDraft = (
  draft: CampaignAllocationSettingsDraft,
  bounds: CampaignAllocationSettingsView['bounds']
): CampaignAllocationSettingsParse => {
  const errors: CampaignAllocationSettingsParse['errors'] = {}
  const values = {} as CampaignAllocationSettings

  // Ordre des criteres : exactement trois cles connues et distinctes, comme le 422 serveur.
  // Une liste corrompue est REFUSEE (et non silencieusement remise au defaut) — sinon un
  // calcul partirait avec un ordre que l'ecran n'affiche pas.
  if (isConsumableCriteriaOrderValid(draft?.consumableCriteriaOrder)) {
    values.consumableCriteriaOrder = [...draft.consumableCriteriaOrder]
  } else {
    errors.consumableCriteriaOrder = 'invalid'
  }

  // Une case a cocher n'a pas de saisie invalide : elle vaut ce qu'elle vaut.
  values.scenarioBEnabled = Boolean(draft?.scenarioBEnabled)

  CAMPAIGN_ALLOCATION_SETTINGS_KEYS.forEach(key => {
    const raw = String(draft?.[key] ?? '')
      .trim()
      .replace(',', '.')
    const bound = bounds?.[key]

    // Reglage inconnu de cette API (pas de borne declaree) : hors du jeu, jamais une erreur.
    if (bound == null && CAMPAIGN_ALLOCATION_SETTINGS_OPTIONAL_KEYS.includes(key)) return

    if (raw === '') {
      // Seuls les deux seuils ont un sens a vide : la regle est desactivee.
      if (bound?.nullable) {
        ;(values as any)[key] = null
      } else {
        errors[key] = 'invalid'
      }

      return
    }

    const parsed = Number(raw)
    if (!Number.isFinite(parsed)) {
      errors[key] = 'invalid'

      return
    }
    if (CAMPAIGN_ALLOCATION_SETTINGS_INTEGER_KEYS.includes(key) && !Number.isInteger(parsed)) {
      errors[key] = 'invalid'

      return
    }
    if (bound != null && (parsed < bound.min || parsed > bound.max)) {
      errors[key] = 'range'

      return
    }
    ;(values as any)[key] = parsed
  })

  return { values: Object.keys(errors).length > 0 ? null : values, errors }
}

/** Deux ordres de criteres identiques, position par position ? */
export const isSameConsumableCriteriaOrder = (
  a: readonly ConsumableCriterion[] | null | undefined,
  b: readonly ConsumableCriterion[] | null | undefined
): boolean => {
  const left = normalizeConsumableCriteriaOrder(a)
  const right = normalizeConsumableCriteriaOrder(b)

  return left.every((criterion, index) => criterion === right[index])
}

/** Au moins une des six valeurs — ou l'ORDRE des criteres — differe du laboratoire ? */
export const isAllocationSettingsOverridden = (
  values: CampaignAllocationSettings | null,
  effective: CampaignAllocationSettings | undefined
): boolean =>
  values != null &&
  effective != null &&
  (CAMPAIGN_ALLOCATION_SETTINGS_KEYS.some(key => values[key] !== effective[key]) ||
    !isSameConsumableCriteriaOrder(
      values.consumableCriteriaOrder,
      effective.consumableCriteriaOrder
    ))

/**
 * Reglages TELS QU'ILS ONT SERVI, restitues par la proposition (`summary.settings`) et par la
 * trace du calcul (`params.settings`). Partiel a dessein : un calcul ANTERIEUR au lot 1 n'a que
 * les 3 forfaits cote `summary`, et `{}` cote `params`.
 */
export type CampaignAllocationSettingsUsed = Partial<CampaignAllocationSettings> & {
  /** `true` : au moins une valeur a ete modifiee pour ce calcul (≠ fiche du laboratoire). */
  overridden?: boolean
}

/**
 * Corps `settings` envoye au calcul ET a l'export XLSX. L'ordre des criteres n'y figure QUE
 * si l'API le declare (`view.criteria`) : poser une cle inconnue sur une API anterieure ferait
 * un 422 au bout d'un calcul long, pour un reglage que l'ecran n'affichait meme pas.
 */
export type CampaignAllocationSettingsPayload = CampaignAllocationNumericSettings & {
  consumableCriteriaOrder?: ConsumableCriterion[]
}

/**
 * « ecart › envois › catalogue » : l'ordre rendu lisible, avec le libelle de l'appelant.
 *
 * Sans `catalog` explicite, on restitue EXACTEMENT les criteres portes par `order` : completer
 * d'office un ordre a trois cles avec `spread` attribuerait a un calcul deja joue un critere
 * qu'il n'a jamais applique. Le bloc de reglage, lui, passe le catalogue du serveur — c'est la
 * qu'un critere manquant doit apparaitre.
 */
export const consumableCriteriaOrderText = (
  order: readonly ConsumableCriterion[] | null | undefined,
  label: (criterion: ConsumableCriterion) => string,
  catalog?: readonly ConsumableCriterion[]
): string =>
  normalizeConsumableCriteriaOrder(
    order,
    catalog ?? (isConsumableCriteriaOrderValid(order) ? order : undefined)
  )
    .map(label)
    .join(CONSUMABLE_CRITERIA_SEPARATOR)

/**
 * Produit PLV proposable d'un laboratoire (`GET partner_campaigns/product_catalog`), tel que
 * l'assistant campagne le liste. Declare ICI et non dans l'ecran qui l'affiche : la liste des
 * produits est consommee par le bloc de selection, par l'assistant et par le calcul — la faire
 * transiter par un composant creait un cycle d'imports entre eux.
 */
export type CatalogProduct = {
  id: number
  name: string
  /**
   * NATURE de la reference (`StoreBagProductNature` cote API) : `consommable` (expedie a chaque
   * campagne), `plv_fixe` (materiel pose, une seule expedition) ou `radio_virtuel` (pure
   * preference). Elle range la reference dans la bonne colonne de l'ecran de calcul et decide
   * de sa pre-selection.
   */
  type: ProductNature | string
  priceHt: number
  purchasePrice: number
  /** Stock boutique (CS-Cart) — null : pas de produit boutique (radio) ou boutique KO. */
  stock: number | null
  /**
   * Enveloppe du produit. `null` = reference pas rangee : en mode « deux enveloppes » elle est
   * eligible aux DEUX et consomme le Generique en premier. C'est un defaut de parametrage a
   * corriger dans la fiche produit, pas une valeur neutre — d'ou le signalement en rouge.
   */
  campaignAxis?: 'gx' | 'bs' | null
}

/** Axe d'une campagne a deux enveloppes : `gx` = Generique, `bs` = Biosimilaire. */
export type ProposalAxisKey = 'gx' | 'bs'

/** Les deux axes, dans l'ordre des colonnes budget du fichier (1 = GX, 2 = BS). */
export const PROPOSAL_AXIS_KEYS: readonly ProposalAxisKey[] = ['gx', 'bs'] as const

/**
 * Plafond d'officines d'un CALCUL dual (`CampaignAllocationService::MAX_CIPS_DUAL_AXIS`), le meme
 * que le plafond de LECTURE du fichier (`CampaignAllocationFileService::MAX_AMOUNT_ROWS`) depuis
 * le 02/11/2026.
 */
export const MAX_CIPS_DUAL_AXIS = 8000

/** Produit retenu par le moteur sur un axe (PLV fixe ou consommable). */
export type ProposalAxisProduct = {
  productId: number
  name: string
  quantity: number
  unitPrice: number
  total: number
  /** PLV fixe deja posee chez l'officine (suffixe « (deja en place) » deja dans `detail`). */
  alreadyInstalled?: boolean
  /** Nouvelle pose (lot F non implemente : toujours `false` aujourd'hui). */
  newPose?: boolean
}

/**
 * Canal servi sur UN axe. Minuscules cote moteur ; l'affichage se construit cote front.
 * `radio_plv` : souhait ponctuel « radio + sacs » de l'officine (08/10/2026), l'enveloppe porte
 * des sacs ET une part de radio (le total de l'officine est partage a peu pres moitie-moitie).
 */
export type ProposalAxisMode = 'plv' | 'radio' | 'radio_plv' | 'none'

/** Un axe (GX ou BS) d'une ligne de proposition dual. */
export type ProposalAxis = {
  /** Montant DEMANDE sur cet axe (colonne du fichier). 0 = axe non servi. */
  budget: number
  allocated: number
  /** Reliquat accepte (`gap` ≡ `remainder`). */
  gap: number
  mode: ProposalAxisMode
  newPose: boolean
  fixedPlvSelected: ProposalAxisProduct[]
  consumableSelected: ProposalAxisProduct[]
  amountFixedPlv: number
  amountConsumable: number
  amountRadio: number
  /** Phrase deja rendue par le serveur (prefixe « [HORS PREFERENCE] » compris) : afficher telle quelle. */
  detail: string
  /**
   * Cout ISOLE de cet axe : forfait logistique OU gestion radio — les deux, la gestion radio
   * remisee, sur un axe `radio_plv`.
   */
  cost: number
  outOfPreference: boolean
  /** `plv` | `radio_en_place` | `aucun_produit_eligible` | `sans_budget`. */
  reason: string
  /** 1 (GX) | 2 (BS) quand l'axe porte de la radio (`radio` ou `radio_plv`), sinon `null`. */
  soundProgramIndex: number | null
  /** Trace de la regle « references differees » (05/09/2026) ; absente sur un calcul ancien. */
  deferredUsed?: boolean
  gapWithoutDeferred?: number | null
}

/** Statut d'une regle sur une ligne (05/09/2026) : respectee / non respectee / sans objet. */
export type ProposalRuleStatus = 'ok' | 'ko' | 'na'

/** Constat d'UNE regle sur UNE ligne — « une regle, une colonne ». */
export type ProposalRuleResult = {
  /** Cle STABLE de la regle (`rulesCatalog[].key`). */
  key: string
  status: ProposalRuleStatus
  /** Constat deja redige par le serveur, a afficher tel quel en infobulle. */
  detail: string
}

/** Une regle du catalogue du calcul : construit l'en-tete de sa colonne. */
export type ProposalRuleCatalogEntry = {
  key: string
  /** Libelle long (en-tete de colonne du classeur). */
  label: string
  /** Libelle court (en-tete de colonne de l'ecran), repli quand la cle i18n manque. */
  short: string
  /** Parametre effectif (seuil, plafond…), `null` quand la regle n'en a pas. */
  parameter: string | null
}

/**
 * Cles i18n des libelles COURTS des regles connues. Une regle que le front ne connait pas
 * encore affiche le `short` du serveur : une colonne de plus cote API ne casse rien ici.
 */
export const PROPOSAL_RULE_I18N_KEYS: Record<string, string> = {
  radio_preference: 'components.partnerCampaign.proposal.rule.radio_preference',
  radio_threshold: 'components.partnerCampaign.proposal.rule.radio_threshold',
  fixed_plv_in_place: 'components.partnerCampaign.proposal.rule.fixed_plv_in_place',
  preferences: 'components.partnerCampaign.proposal.rule.preferences',
  max_units: 'components.partnerCampaign.proposal.rule.max_units',
  round_of_three: 'components.partnerCampaign.proposal.rule.round_of_three',
  deferred_threshold: 'components.partnerCampaign.proposal.rule.deferred_threshold',
  zero_gap: 'components.partnerCampaign.proposal.rule.zero_gap',
  stock: 'components.partnerCampaign.proposal.rule.stock',
}

/** Cles i18n des EXPLICATIONS des regles connues (infobulle de l'en-tete de colonne). */
export const PROPOSAL_RULE_HELP_I18N_KEYS: Record<string, string> = {
  radio_preference: 'components.partnerCampaign.proposal.ruleHelp.radio_preference',
  radio_threshold: 'components.partnerCampaign.proposal.ruleHelp.radio_threshold',
  fixed_plv_in_place: 'components.partnerCampaign.proposal.ruleHelp.fixed_plv_in_place',
  preferences: 'components.partnerCampaign.proposal.ruleHelp.preferences',
  max_units: 'components.partnerCampaign.proposal.ruleHelp.max_units',
  round_of_three: 'components.partnerCampaign.proposal.ruleHelp.round_of_three',
  deferred_threshold: 'components.partnerCampaign.proposal.ruleHelp.deferred_threshold',
  zero_gap: 'components.partnerCampaign.proposal.ruleHelp.zero_gap',
  stock: 'components.partnerCampaign.proposal.ruleHelp.stock',
}

/**
 * DECOMPOSITION du forfait d'une ligne (remise radio + PLV, 02/10/2026) :
 * `total` = `radioFee` + `logisticsFee` − `radioPlvDiscount` + `setupFee` = `costTotal` (le net).
 *
 * - `radio`     : gestion radio seule ;
 * - `plv`       : forfait logistique seul ;
 * - `radio_plv` : les DEUX, la gestion radio remisee (`radioPlvDiscount`, montant POSITIF) ;
 * - `none`      : ligne rejetee ou sans cout — tout a 0.
 *
 * La pose (`setupFee`) s'ajoute sans remise. Le front n'additionne rien : il affiche.
 */
export type ProposalCostDetail = {
  mode: 'radio' | 'plv' | 'radio_plv' | 'none'
  radioFee: number
  logisticsFee: number
  radioPlvDiscount: number
  setupFee: number
  total: number
}

/**
 * SCENARIO B d'une ligne (05/09/2026) : la meme repartition SANS contrainte de stock —
 * « l'écart si on a tous les produits sélectionnés en stock ». `differs` = l'officine a ete
 * contrainte par le stock disponible : un reassort placerait davantage de son montant. Quand
 * `differs` est faux, `axes` et les totaux sont ceux de la ligne servie.
 */
export type ProposalUnlimited = {
  axes: Record<ProposalAxisKey, ProposalAxis>
  budgetTotal: number
  allocatedTotal: number
  gapTotal: number
  costTotal: number
  /** Decomposition de `costTotal` sur le scenario B ; absente sur un calcul ancien. */
  costDetail?: ProposalCostDetail
  differs: boolean
  /** Les memes regles, constatees sur le scenario B. */
  rules?: ProposalRuleResult[]
}

/** Totaux du scenario B dans le resume dual. */
export type ProposalUnlimitedSummary = {
  allocatedGx: number
  allocatedBs: number
  gapGx: number
  gapBs: number
  remainderTotal: number
  /** Officines dont le montant place augmenterait avec un reassort. */
  stockConstrainedCount: number
}

/** Anomalie de LECTURE du fichier de montants (jamais bloquante, `report-4.md` §3.1). */
export type ProposalAnomalyReason =
  | 'fichier_sans_entete'
  | 'budget_interprete_comme_montant_gx'
  | 'cip_invalide'
  | 'cip_doublon'
  | 'montant_non_numerique'
  | 'montant_negatif'
  | 'montants_absents'

export type ProposalAnomaly = {
  /** Numero de ligne 1-based (l'en-tete est la ligne 1). `0` = anomalie de FICHIER. */
  line: number
  cip: string | null
  /** Une des 7 valeurs de `ProposalAnomalyReason` — un motif inconnu s'affiche brut. */
  reason: ProposalAnomalyReason | string
}

/** Contexte officine renvoye en dual (indexe par CIP), pour la restitution a l'ecran. */
export type ProposalPharmacyContext = {
  city: string | null
  postalCode: string | null
  cluster: string | null
  radioRefused: boolean
}

/** Officine d'une proposition — champs communs aux deux modes. */
type ProposalRowBase = {
  cip: string
  companyId: number | null
  name: string | null
  found: boolean
  soundActive: boolean
  segmentId: number | null
  /** `''` | `cip_introuvable` | `hors_perimetre` (mode single : phrase du moteur). */
  reason: string
}

/**
 * Ligne de proposition. Les champs `channel` / `budget` / `plvProducts` n'existent qu'en
 * mode SINGLE ; `axes` et les totaux n'existent qu'en mode `dual_axis`. Le rendu choisit
 * la forme via `isDualProposal()`, jamais en testant un champ au hasard.
 */
export type ProposalRow = ProposalRowBase & {
  // --- mode single
  /** 'sound_plv' = multi-canal (DEV-24) : radio ET PLV servies dans la meme enveloppe. */
  channel?: 'sound' | 'plv' | 'sound_plv' | 'none'
  budget?: number
  budgetPerPharmacy?: number
  plvProducts?: { productId: number; name: string; quantity: number; total: number }[]
  // --- mode dual_axis
  axes?: Record<ProposalAxisKey, ProposalAxis>
  /** Cout laboratoire TOTAL de la ligne, DEDOUBLONNE GX+BS (jamais la somme des deux `cost`). */
  costTotal?: number
  /** Decomposition de `costTotal` (radio, logistique, remise, pose) ; absente sur un calcul ancien. */
  costDetail?: ProposalCostDetail
  /**
   * COUT DE FOURNITURE GX+BS : ce que le laboratoire sort de l'entrepot pour cette officine.
   * Additionne sans dedoublonnage — contrairement au forfait, la matiere d'un axe s'ajoute a
   * celle de l'autre. Le materiel deja en place et la radio n'y pesent rien : ils ne
   * s'expedient pas. Absent sur un calcul anterieur au 09/09/2026.
   */
  supplyCostTotal?: number
  budgetTotal?: number
  allocatedTotal?: number
  gapTotal?: number
  /** Constat des regles sur la ligne servie (05/09/2026) ; absent sur un calcul ancien. */
  rules?: ProposalRuleResult[]
  /** Scenario B (stock illimite) de la ligne ; absent sur un calcul ancien. */
  unlimited?: ProposalUnlimited
}

/** Ligne dual garantie (apres `isDualProposal()`). */
export type ProposalDualRow = ProposalRow & {
  axes: Record<ProposalAxisKey, ProposalAxis>
}

/** Resume d'une proposition a enveloppe unique. */
export type ProposalSingleSummary = {
  budgetPerPharmacy: number
  allocated: number
  budgetMaxTotal: number
  countSound: number
  countPlv: number
  /** Pharmacies servies en radio ET en PLV (multi-canal). */
  countBoth?: number
  countNone: number
  countNotFound: number
  /**
   * Officines EXISTANTES mais hors du perimetre du laboratoire (raison `hors_perimetre`) :
   * jamais allouees, et comptees a part — ni « introuvables », ni « sans budget ».
   * Absent = API anterieure au lot P-bis.
   */
  countOutOfScope?: number
  /** Nombre d'anomalies de lecture (lot 4) — present aussi en single. */
  countAnomalies?: number
  soundProgramCount?: number
}

/**
 * Une reference a EXPEDIER pour servir la proposition (`summary.stockNeeds`, 04/09/2026) :
 * consommables retenus et PLV fixes en nouvelle pose — jamais un dispositif « deja en place »,
 * jamais une radio. `stock` = stock boutique au moment du calcul, `engaged` = deja alloue par
 * les programmes en cours, `available` = stock - engage, `missing` = ce qui manque pour servir
 * la proposition. `null` = stock inconnu / non suivi (ne jamais afficher 0 a la place).
 */
export type ProposalStockNeed = {
  productId: number
  name: string
  isFixedPlv: boolean
  position: number
  pharmacies: number
  quantity: number
  stock: number | null
  remaining: number | null
  engaged?: number
  available?: number | null
  /**
   * MANQUE pour servir le SCENARIO B (05/09/2026) : la ligne servie ne depasse jamais le
   * disponible, seul B peut manquer — c'est ce qu'il faut reapprovisionner. `null` = stock inconnu.
   */
  missing: number | null
  /** Unites que la meme repartition expedierait sans contrainte de stock (scenario B). */
  quantityUnlimited?: number
  pharmaciesUnlimited?: number
}

/**
 * PLV FIXE retenue par la proposition (`summary.fixedPlvNeeds`, 08/09/2026) : materiel DEJA EN
 * PLACE chez l'officine — jamais expedie, donc jamais dans `stockNeeds`, qui ne dit que ce qui
 * sort de l'entrepot. `quantity` = unites creditees, `amount` = montant facture en prestation
 * d'affichage, les deux enveloppes cumulees.
 */
export type ProposalFixedPlvNeed = {
  productId: number
  name: string
  position: number
  pharmacies: number
  quantity: number
  amount: number
  /** Unités À EXPÉDIER (nouvelles poses, 10/09/2026) ; le reste est déjà en place. Absent sur un calcul ancien. */
  newPose?: number
}

/** Resume d'une proposition a deux enveloppes (`soundProgramCount === 2`). */
export type ProposalDualSummary = {
  soundProgramCount: number
  countGx: number
  countBs: number
  countNotFound: number
  countOutOfScope: number
  /** 100 % des officines du perimetre sont sans montant sur cet axe : fichier mal colonne ? */
  axisGxEntirelyWithoutBudget: boolean
  axisBsEntirelyWithoutBudget: boolean
  radiosGx: number
  radiosBs: number
  plvGx: number
  plvBs: number
  budgetGxTotal: number
  budgetBsTotal: number
  allocatedGx: number
  allocatedBs: number
  gapGx: number
  gapBs: number
  remainderGx: number
  remainderBs: number
  remainderTotal: number
  /** Cout laboratoire total (deja dedoublonne ligne par ligne). */
  costTotal: number
  /**
   * Officines servies en radio ET en PLV, et le total des remises qu'elles portent — DEJA
   * deduit de `costTotal`. Absents sur un calcul anterieur au 02/10/2026.
   */
  radioPlvCount?: number
  radioPlvDiscountTotal?: number
  /** Cout de FOURNITURE total (matiere expediee) ; absent sur un calcul ancien. */
  supplyCostTotal?: number
  /**
   * Couts PAR AXE : forfait isole (`costGx`/`costBs`) et matiere expediee. Leur somme n'est
   * PAS `costTotal`, qui dedoublonne par officine — une radio qui sert les deux axes n'est
   * gérée qu'une fois. Absents sur un calcul anterieur au 09/09/2026.
   */
  costGx?: number
  costBs?: number
  supplyCostGx?: number
  supplyCostBs?: number
  /** Ids de produits consommes a stock ILLIMITE / INCONNU (alerte nominative). */
  stockUnlimitedProductIds: number[]
  /** Ids de produits a stock CONNU integralement consommes pendant ce calcul. */
  stockExhaustedProductIds: number[]
  /** Stock NECESSAIRE : unites a expedier par reference (absent sur un calcul ancien). */
  stockNeeds?: ProposalStockNeed[]
  /** PLV FIXES creditees par reference (absent sur un calcul anterieur au 08/09/2026). */
  fixedPlvNeeds?: ProposalFixedPlvNeed[]
  countAnomalies?: number
  /**
   * Regles de calcul au moment du calcul (dual seulement). Six valeurs + `overridden` depuis
   * le lot 1 ; un calcul ANTERIEUR n'en porte que trois — d'ou le type partiel.
   */
  settings?: CampaignAllocationSettingsUsed
  /** Scenario B — stock illimite (05/09/2026) ; absent sur un calcul ancien. */
  unlimited?: ProposalUnlimitedSummary
  /** Catalogue des regles du calcul : une colonne par entree. Absent sur un calcul ancien. */
  rulesCatalog?: ProposalRuleCatalogEntry[]
  /** Nombre de lignes « non » par regle, sur la ligne servie puis sur le scenario B. */
  rulesKoCount?: Record<string, number>
  rulesKoCountUnlimited?: Record<string, number>
}

export type ProposalSummary = ProposalSingleSummary | ProposalDualSummary

/** Produit du catalogue tel que renvoye par la proposition (forme etendue en dual). */
export type ProposalCatalogProduct = {
  id: number
  name: string
  isFixedPlv?: boolean
  axis?: ProposalAxisKey | null
  position?: number
  defaultPrice?: number
  maxUnitsPerPharmacy?: number | null
  stock?: number | null
  active?: boolean
  deferred?: boolean
}

/** Reponse de `POST partner_campaigns/allocation_proposal`. */
export type ProposalResult = {
  rows: ProposalRow[]
  summary: ProposalSummary
  products?: ProposalCatalogProduct[]
  /** Toujours presente depuis le lot 4 (liste vide en single sans anomalie). */
  anomalies?: ProposalAnomaly[]
  /** dual_axis SEULEMENT, indexe par CIP. */
  pharmacyContext?: Record<string, ProposalPharmacyContext>
  /** Mode du LABORATOIRE au moment du calcul (lot 5). Absent = API anterieure. */
  mode?: CampaignAllocationMode
  /** Trace persistee du calcul (lot 5) — `null` quand `persist: false`. */
  runId?: number | null
}

/** Proposition dual garantie (apres `isDualProposal()`). */
export type ProposalDualResult = ProposalResult & {
  rows: ProposalDualRow[]
  summary: ProposalDualSummary
}

/**
 * La proposition est-elle a DEUX ENVELOPPES ?
 *
 * `mode` (lot 5) fait foi ; le repli sur `summary.soundProgramCount === 2` couvre une API
 * anterieure au lot 5 qui rendrait deja le resume dual (lot 2b) sans la cle `mode`.
 */
export const isDualProposal = (result: ProposalResult | null): result is ProposalDualResult =>
  result != null &&
  (result.mode === 'dual_axis' || (result.summary as ProposalDualSummary)?.soundProgramCount === 2)

/**
 * Total ALLOUE d'une proposition, quel que soit le mode — c'est lui qui alimente
 * `budgetTotal` a la creation de la campagne. En dual la cle `allocated` n'existe pas :
 * la lire directement rendait `undefined` et perdait le budget total de la campagne.
 */
export const proposalAllocatedTotal = (result: ProposalResult | null): number | null => {
  if (result == null) return null
  if (isDualProposal(result)) {
    return (result.summary.allocatedGx ?? 0) + (result.summary.allocatedBs ?? 0)
  }

  const allocated = (result.summary as ProposalSingleSummary).allocated

  return typeof allocated === 'number' ? allocated : null
}

/**
 * Les deux chiffres qu'on compare d'un calcul a l'autre : le RELIQUAT total (ce que le moteur
 * n'a pas su placer) et le COUT laboratoire. Ce sont les deux seuls qu'on relance un calcul pour
 * faire bouger — le reste (nombre d'officines, montants demandes) vient du fichier et ne change
 * pas quand on ajuste une regle.
 */
export type ProposalComparisonFigures = {
  gap: number
  cost: number
}

/**
 * Chiffres comparables d'une proposition. `null` en enveloppe unique : le resume `single` ne
 * porte ni reliquat ni cout laboratoire — mieux vaut ne rien comparer que comparer autre chose.
 */
export const proposalComparisonFigures = (
  result: ProposalResult | null
): ProposalComparisonFigures | null => {
  if (result == null || !isDualProposal(result)) return null
  const summary = result.summary

  return {
    gap: (summary.gapGx ?? 0) + (summary.gapBs ?? 0),
    cost: summary.costTotal ?? 0,
  }
}

/**
 * Trace persistee d'un calcul de repartition (`GET partner_campaigns/allocation_runs`).
 * `requestedBy`, `partnerCampaignId` et `fileKey` sont TOUJOURS presents (a `null` le cas
 * echeant) : le serveur desactive `skip_null_values` sur cette ressource (D-5-3).
 */
export type AllocationRun = {
  id: number
  /** ISO 8601 (DateTimeInterface::ATOM). */
  createdAt: string
  mode: CampaignAllocationMode
  requestedBy: { id: number; fullName: string } | null
  partnerCampaignId: number | null
  /**
   * Cle S3 du classeur produit par l'export. Directement reutilisable comme `keyFile` de
   * `allocation_validate` / `allocation_import` (prefixe `uploads/pharmacies/`, D-5-7).
   */
  fileKey: string | null
  hasFile: boolean
  params: {
    source: 'file' | 'filters'
    budgetPerPharmacy: number | null
    soundProgramLabels: string[]
    /**
     * Regles de calcul FIGEES a l'entree (lot 1) — ce qui rend le calcul reproductible.
     * Un run ANTERIEUR au lot 1 rend `{}` : ne rien afficher plutot qu'inventer des valeurs.
     */
    settings?: CampaignAllocationSettingsUsed
  }
  /** Resume du moteur TEL QUEL + `anomalies` (tronquees a 200). */
  summary: ProposalSummary & { anomalies?: ProposalAnomaly[] }
}

/** Detail par axe rendu par `allocation_validate` en dual (`report-4.md` §3.3). */
export type ValidationAxes = Record<ProposalAxisKey, { count: number; budget: number }>

/**
 * Cles i18n des libelles par defaut des deux programmes radio (« Radio GX » / « Radio BS »).
 * Le libelle est SAISISSABLE : ces valeurs ne sont que le defaut, resolu par l'appelant —
 * aucune chaine d'interface n'est ecrite en dur ici.
 */
export const AXIS_PROGRAM_LABEL_KEYS: Record<ProposalAxisKey, string> = {
  gx: 'components.partnerCampaign.proposal.axisProgramLabelGx',
  bs: 'components.partnerCampaign.proposal.axisProgramLabelBs',
}
